/**
 * ============================================================================
 * Iron Meridian - Auto-Save Indicator Component
 * ============================================================================
 * 
 * Shows a brief visual indicator when the game auto-saves.
 * Displays in the top-right corner with a fade animation.
 * 
 * ============================================================================
 */

import { useState, useEffect, useCallback } from 'react'

// ============================================================================
// SAVE STATUS SERVICE
// ============================================================================

class SaveStatusService {
  constructor() {
    this._listeners = new Set()
    this._lastSave = null
    this._isSaving = false
    this._saveCount = 0
  }

  /**
   * Report that a save has started
   */
  savingStarted() {
    this._isSaving = true
    this._emit({ type: 'saving' })
  }

  /**
   * Report that a save completed successfully
   */
  saveCompleted() {
    this._isSaving = false
    this._lastSave = new Date()
    this._saveCount++
    this._emit({ type: 'saved', timestamp: this._lastSave })
  }

  /**
   * Report that a save failed
   */
  saveFailed(error) {
    this._isSaving = false
    this._emit({ type: 'error', error })
  }

  /**
   * Subscribe to save status changes
   * @param {Function} callback
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this._listeners.add(callback)
    return () => this._listeners.delete(callback)
  }

  _emit(event) {
    for (const listener of this._listeners) {
      try {
        listener(event)
      } catch (err) {
        console.error('[SaveStatus] Listener error:', err)
      }
    }
  }

  get lastSave() {
    return this._lastSave
  }

  get isSaving() {
    return this._isSaving
  }
}

export const saveStatus = new SaveStatusService()

// ============================================================================
// REACT HOOK
// ============================================================================

/**
 * Hook for tracking save status
 */
export function useSaveStatus() {
  const [status, setStatus] = useState({ type: 'idle' })
  const [showIndicator, setShowIndicator] = useState(false)

  useEffect(() => {
    const unsubscribe = saveStatus.subscribe((event) => {
      setStatus(event)
      
      if (event.type === 'saved') {
        // Show indicator briefly
        setShowIndicator(true)
        setTimeout(() => setShowIndicator(false), 2000)
      } else if (event.type === 'saving') {
        setShowIndicator(true)
      } else if (event.type === 'error') {
        setShowIndicator(true)
        setTimeout(() => setShowIndicator(false), 3000)
      }
    })

    return unsubscribe
  }, [])

  return { status, showIndicator }
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Auto-save indicator component
 * Shows briefly when game is saved
 */
export function AutoSaveIndicator() {
  const { status, showIndicator } = useSaveStatus()

  if (!showIndicator) return null

  const isSaving = status.type === 'saving'
  const isError = status.type === 'error'

  return (
    <div 
      className={`
        fixed top-4 right-4 z-50
        flex items-center gap-2 px-3 py-2
        rounded-lg shadow-lg backdrop-blur-sm
        transition-all duration-300
        ${isSaving 
          ? 'bg-blue-500/20 border border-blue-500/30' 
          : isError 
            ? 'bg-red-500/20 border border-red-500/30'
            : 'bg-green-500/20 border border-green-500/30'
        }
        animate-fade-in
      `}
    >
      {isSaving ? (
        <>
          <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-xs text-blue-400 font-medium">Saving...</span>
        </>
      ) : isError ? (
        <>
          <span className="text-red-400">⚠️</span>
          <span className="text-xs text-red-400 font-medium">Save failed</span>
        </>
      ) : (
        <>
          <span className="text-green-400">✓</span>
          <span className="text-xs text-green-400 font-medium">Saved</span>
        </>
      )}
    </div>
  )
}

export default AutoSaveIndicator
